import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import FlashMessageService from '../../services/FlashMessageService';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import JourneyDetailsInfo from './overall/list/JourneyDetailsInfo';
import JourneyCarousel from './overall/carousel/JourneyCarousel';
import JourneyPath from './overall/path/JourneyPath';
import JourneyProp from './propTypes/journeyProp';
import { useModal, useScrollAnchor } from '../../services/customHooks';
import JourneyContent from './overall/content/JourneyContent';
import AppProductProp from '../profile/propTypes/appProductProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import HardSkillProp from '../challenges/propTypes/hardSkillProp';
import TrophyProp from './propTypes/trophyProp';
import PerformanceProp from '../profile/propTypes/performanceProp';
import ChartsProp from '../profile/propTypes/chartsProp';
import CertificateProp from '../academy/propTypes/certificateProp';
import CongratsModal from './modals/CongratsModal';
import {
  compose,
  displayTrophyName,
  isItemFree,
  isJourneyUnlocked,
  isAwaitingPaymentConfirmation,
  JOURNEY_STATUS,
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_TYPE,
  JOURNEY_TASK_STATUS,
  normalizeJourney,
  TROPHY_CONFIG,
} from '../../services/utilityFunctions';
import { buildAcademySuccessModalData } from '../../services/academySuccessModal';
import ServicesContext from '../../contextProviders/ServicesContext';
import Notifications from '../../services/notifications';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import GoogleAnalytics, {
  ENROLL_SOURCE,
  ENROLL_TYPE,
} from '../../services/googleAnalytics';
import JourneyStepDone from './overall/JourneyStepDone';
import EndJourneyModal from './modals/EndJourneyModal';
import JourneyPlaceholder from './overall/JourneyPlaceholder';
import UserProp from '../../commonComponents/propTypes/userProp';
import JourneyOverallRewards from './overall/stats/JourneyOverallRewards';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import CurrencyProp from '../profile/propTypes/currencyProp';
import AcademyPaymentModal from '../../commonComponents/modal/AcademyPaymentModal';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import ShopModalOrderComplete from '../shop/shopItems/ShopModalOrderComplete';
import OrganizationProp from '../../commonComponents/propTypes/organizationProp';
import withCSRF from '../../commonComponents/withCSRF';
import Loader from '../../services/loader';
import JourneyItem from './JourneyItem';
import ContentInacessibleModal from '../../commonComponents/modal/ContentInacessibleModal';
import WaitingApprovalModal from '../../commonComponents/modal/WaitingApprovalModal';
import EnrollForm from '../../commonComponents/forms/EnrollForm';
import LoginModal from '../signIn/modals/LoginModal';

/** Auxiliary functions */
/**
 * Check if we should update the user context
 * @param currentUser {UserProp}
 * @param journey {JourneyProp}
 * @returns {boolean}
 */
const updateUserCtx = (currentUser, journey) => {
  const { type, level } = currentUser || {};
  const { reward } = journey || {};
  const {
    userType = null,
    userLevel = null,
    competenciesDomains = [],
    isTrainer = false,
    isOperator = false,
  } = reward || {};

  return (
    (userType && type !== userType) ||
    (userLevel && level !== userLevel) ||
    competenciesDomains?.length > 0 ||
    isTrainer ||
    isOperator
  );
};

/**
 * Get the user updated
 * @returns {Promise<*|null>}
 */
const getCurrentUser = async () => {
  try {
    const { data: response } = await axios.get('/api/user/me', {
      headers: {
        'X-Gamification-API': 'Organization',
        'Accept-Version': '1.0.0',
      },
    });
    return response.data;
  } catch (err) {
    console.error(err);
    Notifications.showErrorFromRequest(err);
    return null;
  }
};

const isStepAvailable = step =>
  step.status !== JOURNEY_STEP_STATUS.DONE &&
  step.status !== JOURNEY_STEP_STATUS.LOCKED &&
  step.status !== JOURNEY_STEP_STATUS.WAITING_APPROVAL &&
  step.type !== JOURNEY_STEP_TYPE.TROPHY &&
  step.type !== JOURNEY_STEP_TYPE.CERTIFICATE;

const journeySteps = journey => (journey && journey.steps ? journey.steps : []);

// Return updated steps with certificate
const returnJourneyStepWithCertificate = (journey, userCertificate) => {
  let steps = journey.steps || [];
  const containCertificateStep =
    journey &&
    journey.steps &&
    journey.steps.find(s => s._id === 'stepCertificate');
  if (journey?.reward?.certificate.enabled && !containCertificateStep) {
    let status = JOURNEY_STEP_STATUS.LOCKED;
    if (userCertificate) {
      status = JOURNEY_STEP_STATUS.UNLOCKED;
    }

    steps = steps.concat({
      _id: 'stepCertificate',
      type: JOURNEY_STEP_TYPE.CERTIFICATE,
      pos: steps.length,
      status,
      certificate: userCertificate,
    });
  }
  return steps;
};

const firstStepId = journey => {
  const steps = journeySteps(journey);
  for (const step of steps) {
    if (isStepAvailable(step)) {
      return step._id;
    }
  }
  return null;
};

const nextAvailableStep = (journey, step) => {
  const steps = journeySteps(journey);
  const currentIndex = step ? steps.findIndex(s => s._id === step._id) : 0;
  const startingIndex = currentIndex === -1 ? 0 : currentIndex;
  for (let i = startingIndex; i < steps.length; i += 1) {
    if (isStepAvailable(steps[i])) {
      return steps[i];
    }
  }
  return null;
};

const findItem = (collection, id, attr = '_id') => {
  const item = collection.find(j => j[attr] === id);
  if (id && item) {
    return item;
  }
  return collection.length > 0 ? collection[0] : {};
};

const findStep = (journey, id) => {
  const steps = journeySteps(journey);
  return findItem(steps, id);
};

const setMetaData = (info = {}) => {
  const general = [
    { key: 'description', value: info.description },
    { key: 'keywords', value: (info.keywords || []).join() },
    {
      tag: 'link',
      attr: 'rel',
      key: 'canonical',
      valueKey: 'href',
      value: info.url,
    },
  ];

  const facebook = [
    { attr: 'property', key: 'og:type', value: 'website' },
    { attr: 'property', key: 'og:title', value: info.title },
    { attr: 'property', key: 'og:site_name', value: info.title },
    { attr: 'property', key: 'og:description', value: info.description },
    { attr: 'property', key: 'og:url', value: info.url },
    { attr: 'property', key: 'og:image', value: info.image },
    { attr: 'property', key: 'og:image:secure_url', value: info.image },
    { attr: 'property', key: 'og:image:width', value: 1200 },
    { attr: 'property', key: 'og:image:height', value: 630 },
  ];
  const twitter = [
    { key: 'twitter:card', value: 'summary_large_image' },
    { key: 'twitter:title', value: info.title },
    { key: 'twitter:description', value: info.description },
    { key: 'twitter:image', value: info.image },
  ];

  const clearMeta = () => {
    const removeElement = ({ tag = 'meta', attr = 'name', key }) => {
      const el = document.querySelector(`${tag}[${attr}="${key}"]`);
      if (el) el.remove();
    };

    general.forEach(removeElement);
    facebook.forEach(removeElement);
    twitter.forEach(removeElement);
  };

  const createMeta = () => {
    const createElement = data => {
      const {
        tag = 'meta',
        attr = 'name',
        valueKey = 'content',
        key,
        value,
      } = data;
      const el = document.createElement(tag);
      el.setAttribute(attr, key);
      el.setAttribute(valueKey, value);
      document.head.appendChild(el);
    };

    general.forEach(createElement);
    facebook.forEach(createElement);
    twitter.forEach(createElement);
  };

  clearMeta();
  if (info.title && info.description && info.url && info.image) {
    createMeta();
  }
};
const checkMarkAsDone = selectedStep => {
  let showMarkAsDone =
    selectedStep &&
    selectedStep.type !== JOURNEY_STEP_TYPE.TASK &&
    selectedStep.progress >= 100 &&
    selectedStep.status !== JOURNEY_STEP_STATUS.DONE;

  if (selectedStep.type === JOURNEY_STEP_TYPE.COURSE) {
    if (selectedStep.course && selectedStep.course.isFromExternalProvider) {
      showMarkAsDone =
        selectedStep.course.status === 'finished' &&
        selectedStep.status !== JOURNEY_STEP_STATUS.DONE;
    }
  }
  return showMarkAsDone;
};
/** End of Auxiliary functions */

const JOURNEY_HEADERS = {
  'Accept-Version': '1.0.0',
  'X-Gamification-API': 'Games',
};

const JOURNEY_POPULATE = [
  'survey',
  'game',
  'game.gameType',
  'game.courses',
  'course',
  'trophyCustom',
  'reward.chatGroup',
  'reward.couponDiscount.existentCouponId',
  'session',
];

// React Component
function JourneyOverall(props) {
  const {
    t,
    journey: journeyOpened,
    stepId,
    member,
    countries,
    cards,
    languages,
    experienceDomainSkills,
    hardSkills,
    stats,
    userCertificate,
    customTrophies,
    appProducts,
    pspdfkitLicenseKey,
    vatTypes,
    allLanguages,
    currencies,
    userLanguage,
    directSellingActive,
    orderId,
    redirect_status,
    payment_intent,
    open,
    sessionContext,
    tenant,
    organization,
    _csrf,
    sessionVerifiedParticipation,
    urlBack,
    availableCountries,
    availableCurrencies,
  } = props;

  // Loader
  const loader = useRef(new Loader('#journeyContent'));
  const [isLoading, setLoading] = useState(false);
  // Used to check and update logged user
  const { loggedUser, setLoggedUser } = useContext(LoggedUserContext);
  // Used so we don't update user in first render
  const isAfterFirstRender = useRef(false);

  const [showContentInacessibleModal, setShowContentInacessibleModal] =
    useModal({
      show: false,
      data: null,
    });

  const [showWaitingApprovalModal, setShowWaitingApprovalModal] = useModal({
    show: false,
    data: null,
  });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const ownerId = member ? member._id : null;
  // Setting the certificate, in case of its already generated or not
  const [certificate, setCertificate] = useState(userCertificate);

  const services = useContext(ServicesContext);

  // For anonymous visitors the language is not resolved from an auth token, so
  // forward it explicitly as a query param (same contract as the SSR journey
  // page and JourneysAcademyList) so the API can apply language targeting. Use
  // the SSR-resolved request language (`userLanguage`) rather than the
  // cookie-derived `localLanguage`, which defaults to 'en' on a first anonymous
  // visit before the cookie is set. Only send it when it is a known available
  // language.
  const notLoggedLanguage =
    !loggedUser?._id &&
    userLanguage &&
    languages.some(lang => lang.code === userLanguage)
      ? userLanguage
      : undefined;

  const { contentElement: stepElement, scrollToContent: scrollToStep } =
    useScrollAnchor();
  const waitScrollToStep = () => setTimeout(scrollToStep, 1000);

  const { contentElement: journeyElement } = useScrollAnchor();

  const [modalInfoCongrats, setModalInfoCongrats] = useState({
    show: false,
    data: null,
  });
  const showModalCongrats = step => {
    let imageURL;
    const name = displayTrophyName(t, step.trophy);
    if (step.trophy !== 'custom') {
      imageURL = TROPHY_CONFIG[step.trophy].imageURL;
    } else {
      imageURL =
        step.trophyCustom && step.trophyCustom.icon
          ? step.trophyCustom.icon.thumb
          : t(TROPHY_CONFIG.custom.imageURL);
    }
    setModalInfoCongrats({ show: true, data: { imageURL, name } });
  };
  const hideModalCongrats = () =>
    setModalInfoCongrats({ show: false, data: null });

  const [modalInfoEndJourney, setModalInfoEndJourney] = useState({
    show: false,
    data: null,
  });
  const showModalEndJourney = journey =>
    setModalInfoEndJourney({ show: true, data: journey });
  const hideModalEndJourney = () =>
    setModalInfoEndJourney({ show: false, data: null });

  const [selectedJourney, setSelectedJourney] = useState(
    normalizeJourney(services, {
      ...journeyOpened,
      steps: returnJourneyStepWithCertificate(journeyOpened, certificate),
    }),
  );

  const getJourney = () => {
    setLoading(true);
    let urlJourney = `/api/journey/slug/${journeyOpened.slug}`;
    if (sessionContext) {
      urlJourney += `?sessionContext=${sessionContext}`;
    }
    axios
      .get(urlJourney, {
        headers: JOURNEY_HEADERS,
        params: {
          populate: JOURNEY_POPULATE,
          ...(notLoggedLanguage ? { notLoggedLanguage } : {}),
        },
      })
      .then(({ data: response }) => {
        setSelectedJourney(
          normalizeJourney(services, {
            ...response.data,
            steps: returnJourneyStepWithCertificate(response.data, certificate),
          }),
        );
        setLoading(false);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchJourneys');
        setLoading(false);
      });
  };

  // Update the steps with certificate or not
  useEffect(() => {
    setMetaData(journeyOpened.seo);

    // Anonymous visitors keep the SSR journey: the /api proxy would redirect
    // them to /signin (BRAVON-6329), and the locked view needs no user data.
    if (loggedUser) {
      getJourney();
    }
    // Update ref after first render
    isAfterFirstRender.current = true;
  }, []);

  const [selectedStep, setSelectedStep] = useState(
    findStep(selectedJourney, stepId || firstStepId(selectedJourney)),
  );

  useEffect(() => {
    if (selectedStep?.taskStatus === JOURNEY_TASK_STATUS.PENDING) {
      setShowWaitingApprovalModal({ show: true, data: null });
    }
  }, [selectedStep]);

  // When the journey is completed, it updates de step and then send the user to certificate step
  useEffect(() => {
    const hasPendingStatus = selectedJourney?.steps?.find(
      s => s.taskStatus === JOURNEY_TASK_STATUS.PENDING,
    )?._id;
    const hasInProgressStatus = selectedJourney?.steps?.find(
      s => s.status === JOURNEY_STEP_STATUS.IN_PROGRESS,
    )?._id;
    setSelectedStep(
      findStep(
        selectedJourney,
        hasInProgressStatus || hasPendingStatus || firstStepId(selectedJourney),
      ),
    );
  }, [selectedJourney]);
  // BRAVON-6317: free journeys are locked until the user explicitly starts
  // them (isStarted / status from the backend), unlike the old behavior where
  // free meant unlocked.
  const isEnrolled = isJourneyUnlocked(loggedUser, selectedJourney);
  const isFreeJourney = isItemFree(selectedJourney);
  // BRAVON-6462: already ordered, waiting on a payment an admin confirms. The
  // journey still opens — only the purchase CTA is replaced, since offering it
  // again invites a second purchase of the same journey.
  const awaitingPayment = isAwaitingPaymentConfirmation(
    loggedUser,
    selectedJourney,
  );
  const shouldOpen =
    open &&
    loggedUser &&
    selectedJourney?.userPrice?.total > 0 &&
    !isEnrolled &&
    !awaitingPayment
      ? !!open
      : null;
  const [isShowingModal, setIsShowingModal] = useState(
    !!(orderId && redirect_status && payment_intent) || shouldOpen,
  );
  const showModal = () => {
    if (journeyOpened.isContentAccessible) {
      GoogleAnalytics.enrollClicked(
        ENROLL_TYPE.JOURNEY,
        selectedJourney.title,
        ENROLL_SOURCE.DETAIL,
        true,
      );
      setIsShowingModal(true);
    } else {
      setShowContentInacessibleModal({ show: true, data: null });
    }
  };
  const hideModal = () => setIsShowingModal(false);

  const [isShowingNextButton, setIsShowingNextButton] = useState(false);
  const showNextButton = () => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      window.scrollTo(0, document.body.scrollHeight);
      setIsShowingNextButton(true);
    }
  };
  const hideNextButton = () => setIsShowingNextButton(false);
  const shouldShowNextButton =
    isShowingNextButton && nextAvailableStep(selectedJourney, selectedStep);

  const [isShowingAll, setIsShowingAll] = useState(!isEnrolled);

  const getUserCertificate = journey =>
    new Promise((resolve, reject) => {
      if (ownerId) {
        // Fetch the certificate -> list, filter by the owner and journey id;
        axios
          .get('/api/certificate/list', {
            headers: JOURNEY_HEADERS,
            params: {
              owner: ownerId,
              journey: journey._id,
            },
          })
          .then(({ data }) => {
            resolve(data.data.length > 0 ? data.data[0] : null);
          })
          .catch(err => {
            console.log(err);
            reject(err);
          });
      }
    });

  const pickStep = id => {
    setSelectedStep(findStep(selectedJourney, id));
    setIsShowingAll(false);
    hideNextButton();
    waitScrollToStep();
  };

  const nextStep = () => {
    setSelectedStep(nextAvailableStep(selectedJourney, selectedStep));
    hideNextButton();
    waitScrollToStep();
  };

  const toggleShowAll = () => {
    setIsShowingAll(prev => !prev);
    hideNextButton();
    waitScrollToStep();
  };

  const isLastStep = journeySteps(loggedUser, selectedJourney).reduce(
    (acc, step) =>
      acc &&
      (step.type === JOURNEY_STEP_TYPE.TROPHY ||
        step.status === JOURNEY_STEP_STATUS.DONE ||
        (selectedStep && selectedStep._id === step._id)),
    true,
  );

  const handleJourneyChange = () => {
    let urlJourney = `/api/journey/slug/${selectedJourney.slug}`;
    if (sessionContext) {
      urlJourney += `?sessionContext=${sessionContext}`;
    }
    axios
      .get(urlJourney, {
        params: {
          populate: JOURNEY_POPULATE,
          ...(notLoggedLanguage ? { notLoggedLanguage } : {}),
        },
        headers: {
          ...JOURNEY_HEADERS,
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        let newJourney = normalizeJourney(services, {
          ...response.data,
          steps: returnJourneyStepWithCertificate(response.data, certificate),
        });
        const newJourneySteps = newJourney.steps || [];
        const newStep = findStep(newJourney, selectedStep._id);

        // Google analytics events and show next button on mobile
        if (!isLastStep && newStep.status === JOURNEY_STEP_STATUS.DONE) {
          showNextButton();
        }

        const previouslyDone = selectedJourney.steps.filter(
          step => step.status === JOURNEY_STEP_STATUS.DONE,
        ).length;
        const previousPercent = previouslyDone / selectedJourney.steps.length;

        const currentlyDone = newJourneySteps.filter(
          step => step.status === JOURNEY_STEP_STATUS.DONE,
        ).length;
        const currentPercent = currentlyDone / newJourneySteps.length;

        if (previouslyDone === 0 && currentlyDone > 0) {
          GoogleAnalytics.journeyStarted(selectedJourney.title);
          FlashMessageService.show(
            t('journey_finishFirstStep'),
            '/images/confetti.svg',
          );
        } else if (previousPercent < 0.6 && currentPercent >= 0.6) {
          FlashMessageService.show(
            t('journey_60Percent'),
            '/images/confetti.svg',
          );
        }
        if (!isLastStep) {
          // check for trophies received on update
          // check on pre-update journey for trophies not done
          const trophy = selectedJourney.steps.find(step => {
            if (
              step.type === JOURNEY_STEP_TYPE.TROPHY &&
              step.status !== JOURNEY_STEP_STATUS.DONE
            ) {
              const updatedStep = newJourneySteps.find(s => s._id === step._id);
              // if it's done in the updated journey, we found a trophy to display
              return (
                updatedStep && updatedStep.status === JOURNEY_STEP_STATUS.DONE
              );
            }
            return false;
          });
          if (trophy) {
            showModalCongrats(trophy);
          }
        } else {
          const updated = normalizeJourney(services, response.data);
          const updatedStep = updated.steps.find(
            s => s._id === selectedStep._id,
          );
          if (updatedStep && updatedStep.status === JOURNEY_STEP_STATUS.DONE) {
            GoogleAnalytics.journeyFinished(selectedJourney.title);
          }
        }
        getUserCertificate(newJourney).then(c => {
          // Updated journey picked, and check the certificate
          newJourney = normalizeJourney(services, {
            ...newJourney,
            steps: returnJourneyStepWithCertificate(newJourney, c),
          });
          if (!certificate && c != null) {
            window.location.reload();
          }
          setCertificate(c);
          setSelectedJourney(newJourney);

          // Get the current step
          const currentStep = newJourney.steps?.find(
            step => step._id === selectedStep._id,
          );

          let sId;
          // Find the next step that is either pending or unlocked/in progress
          for (const s of newJourney.steps) {
            if (
              s.taskStatus === JOURNEY_TASK_STATUS.PENDING ||
              s.status === JOURNEY_STEP_STATUS.UNLOCKED ||
              s.status === JOURNEY_STEP_STATUS.IN_PROGRESS
            ) {
              sId = s._id;
            }
          }

          // If the current step is still in validation, don't update
          if (
            currentStep?.taskStatus === JOURNEY_TASK_STATUS.PENDING &&
            sId === currentStep._id
          ) {
            setShowWaitingApprovalModal({ show: true, data: null });
          } else {
            setSelectedStep(findStep(newJourney, firstStepId(newJourney)));
          }
        });

        /** region JOURNEY FINISHED */
        if (
          response?.data?.status === JOURNEY_STATUS.FINISHED && // if journey finishes
          isAfterFirstRender?.current && // it's not first render
          updateUserCtx(loggedUser, response.data) // we should get the updated logged user
        ) {
          // Update user
          getCurrentUser().then(newUser => setLoggedUser(newUser));
        }
        /** endregion JOURNEY FINISHED */
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchJourneys');
      });
  };

  // this use effect is responsible to show the popup of journey return
  // it's being done here like this we can control the update of the user more easily
  useEffect(() => {
    // Early return if selectedJourney is falsy or status is not FINISHED
    if (selectedJourney?.status !== JOURNEY_STATUS.FINISHED) return;

    const timer = setTimeout(() => {
      showModalEndJourney(selectedJourney);
    }, 300);

    // Cleanup timeout if selectedJourney changes or component unmounts
    return () => clearTimeout(timer);
  }, [selectedJourney]);

  const handleSkipStep = async id => {
    try {
      setLoading(true);
      await axios.put(
        `/api/journey/step/${id}/skip`,
        {},
        {
          headers: {
            ...JOURNEY_HEADERS,
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      Notifications.showNotificationSuccess(
        t('success'),
        t('journey.step.skip.success'),
      );
      await handleJourneyChange();
      setLoading(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchJourneys');
    }
  };

  // BRAVON-6317: start a free journey — registers the user as started and
  // subscribes him to all the sessions contained in the journey steps.
  const handleStartJourney = async () => {
    if (!journeyOpened.isContentAccessible) {
      setShowContentInacessibleModal({ show: true, data: null });
      return;
    }
    try {
      setLoading(true);
      await axios.put(
        `/api/journey/${selectedJourney._id}/start`,
        {},
        {
          headers: {
            ...JOURNEY_HEADERS,
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      Notifications.showNotificationSuccess(
        t('success'),
        t('journey_startSuccess'),
      );
      await handleJourneyChange();
      setLoading(false);
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchJourneys');
    }
  };

  const [showLoginModal, setShowLoginModal] = useModal({
    show: false,
    data: null,
  });
  const {
    contentElement: enrollFormElement,
    scrollToContent: scrollToEnrollForm,
  } = useScrollAnchor();

  // After login/signup from the public journey page: paid journeys reopen the
  // page with the payment modal (?open=true); free ones just reload logged in.
  const handleEnrollComplete = () => {
    window.location.href = `/journey/${journeyOpened.slug}${
      !isItemFree(journeyOpened) ? '?open=true' : ''
    }`;
  };

  // Anonymous visitors are sent to the login/signup form; free journeys start
  // directly (BRAVON-6317); paid ones open the payment modal.
  let enrollAction;
  if (!loggedUser) {
    enrollAction = scrollToEnrollForm;
  } else {
    enrollAction = isFreeJourney ? handleStartJourney : showModal;
  }

  const itemToPay = {
    name: journeyOpened.title,
    userPromotionPrice: journeyOpened.userPromotionPrice,
    userPrice: journeyOpened.userPrice,
    _id: journeyOpened._id,
    image: journeyOpened.image,
    availableCountries: journeyOpened.availableCountries,
  };
  const typeOfItem = {
    field: 'journeys',
    fieldId: 'journey',
  };
  const [showOrderSuccessModal, setShowOrderSuccessModal] = useModal({
    show: false,
    data: null,
  });
  const browserHistory = useHistory();
  const handleReload = () => {
    browserHistory.replace(`${window.location.pathname}`);
    browserHistory.go(0);
  };
  const closeOrderSuccessModal = () =>
    setShowOrderSuccessModal({ show: false, data: null });
  const handleSuccessModal = (paymentMethod, targetUsers, total) => {
    setIsShowingModal(false);
    const successModalData = buildAcademySuccessModalData({
      t,
      itemKey: 'journey',
      paymentMethod,
      total,
      redirectStatus: redirect_status,
      contentAvailableBeforePayment:
        journeyOpened.contentAvailableBeforePayment,
      urlGoBack: '/academy/journeys',
      urlGoToOrder: '/shop/academy/order',
      onReload: handleReload,
      onClose: closeOrderSuccessModal,
    });
    // The confirmation closes on "Ok" without reloading, so the page behind it
    // would keep offering "Enroll" until the next visit. The order exists and
    // the API will report `awaitingPaymentConfirmation` from here on; say so
    // now rather than spend a round trip re-reading what the pop-up just
    // decided. `getJourney` reconciles on the next load.
    if (successModalData.isPendingPayment) {
      setSelectedJourney(previous => ({
        ...previous,
        awaitingPaymentConfirmation: true,
      }));
    }
    setShowOrderSuccessModal({
      show: true,
      data: successModalData,
    });
  };
  return (
    <Title title={t('journey_head_title')}>
      <ContentContainer>
        {showContentInacessibleModal.show && (
          <ContentInacessibleModal
            closeModal={() =>
              setShowContentInacessibleModal({ show: false, data: null })
            }
          />
        )}
        {showWaitingApprovalModal.show && (
          <WaitingApprovalModal
            closeModal={() =>
              setShowWaitingApprovalModal({ show: false, data: null })
            }
          />
        )}
        {showOrderSuccessModal.show && (
          <ShopModalOrderComplete
            closeModal={showOrderSuccessModal.data?.onCloseModal}
            urlGoToOrder={showOrderSuccessModal?.data?.urlGoToOrder}
            urlGoBack={showOrderSuccessModal?.data?.urlGoBack}
            goBackMessage={t('order.success.goBack', t('journeys'))}
            modalMessage={showOrderSuccessModal?.data?.modalMessage}
            confirmMessage={showOrderSuccessModal?.data?.buttonMessage}
            showBack={showOrderSuccessModal?.data?.showBack}
          />
        )}
        {isShowingModal && (
          <AcademyPaymentModal
            item={itemToPay}
            typeOfItem={typeOfItem}
            onClose={hideModal}
            cards={cards}
            currencies={currencies}
            countries={countries}
            orderId={orderId}
            redirectStatus={redirect_status}
            paymentIntent={payment_intent}
            tenant={tenant}
            vatTypes={vatTypes}
            openSuccessModal={handleSuccessModal}
          />
        )}
        {modalInfoCongrats.show && (
          <CongratsModal
            trophy={modalInfoCongrats.data}
            onContinue={() => {
              hideModalCongrats();
              nextStep();
            }}
            onClose={hideModalCongrats}
          />
        )}
        {modalInfoEndJourney.show && (
          <EndJourneyModal
            onClose={hideModalEndJourney}
            journey={modalInfoEndJourney.data}
          />
        )}
        {showLoginModal.show && (
          <LoginModal
            onClose={() => setShowLoginModal({ show: false, data: null })}
            languages={[]}
            showRegisterAccount={false}
            logo={null}
            redirectLink={browserHistory.location.pathname}
            onLoggedIn={handleEnrollComplete}
            ignoreRedirection
          />
        )}
        <SubHeader
          title={t('journey_title')}
          breadcrumbs={[
            { link: '/challenges', text: t('header_perform') },
            { link: '', text: selectedJourney ? selectedJourney.title : '' },
          ]}
        />
        <div id="journeyContent" className="m-content journey-container">
          <div className="full-page-container-with-subheader">
            <div className="row">
              <div className="col-lg-3">
                <JourneyDetailsInfo
                  selected={selectedJourney}
                  selectedStep={selectedStep}
                  isEnrolled={isEnrolled}
                />
              </div>
              <div className="col-lg-9" ref={journeyElement}>
                {selectedJourney._id ? (
                  <>
                    <JourneyItem
                      t={t}
                      journey={selectedJourney}
                      languages={languages}
                      showPromotion
                      // Arendt asked for nothing at all on the price card while
                      // the payment is outstanding — the carousel overlay
                      // carries the status. Withholding `onEnroll` is the only
                      // thing keeping the button off this card.
                      onEnroll={
                        !isEnrolled && !awaitingPayment
                          ? enrollAction
                          : undefined
                      }
                      enrollLabel={
                        isFreeJourney ? t('journey_startJourney') : undefined
                      }
                      isListPage={false}
                    />
                    <JourneyOverallRewards journey={selectedJourney} />
                    <JourneyCarousel
                      journey={selectedJourney}
                      selected={selectedStep}
                      onSelection={pickStep}
                      isShowingAll={isShowingAll}
                      onToggleShowAll={toggleShowAll}
                      isEnrolled={isEnrolled}
                      onEnroll={enrollAction}
                      enrollLabel={
                        isFreeJourney ? t('journey_startJourney') : undefined
                      }
                      awaitingPayment={awaitingPayment}
                    />
                  </>
                ) : (
                  <JourneyPlaceholder />
                )}
              </div>
            </div>
            {!loggedUser && (
              <div
                className="row journey-public-enroll"
                ref={enrollFormElement}
              >
                <div className="col-lg-9 offset-lg-3 col-12">
                  <EnrollForm
                    languages={languages}
                    isPublic
                    onComplete={handleEnrollComplete}
                    onLoginModalOpen={() =>
                      setShowLoginModal({ show: true, data: null })
                    }
                    tenant={tenant}
                    organizationIdentifier={organization?.identifierCode || ''}
                    title={t('journey.public.enroll_title')}
                    buttonTxt={t('journey.public.login_enroll_button')}
                    loginTxt={t('journey.public.signup_enroll_button')}
                    countries={(availableCountries || []).map(country => ({
                      id: country.code,
                      text: country.name,
                    }))}
                    currencies={(availableCurrencies || []).map(currency => ({
                      id: currency.code,
                      text: currency.name,
                    }))}
                    isFromSession
                  />
                </div>
              </div>
            )}
            {selectedJourney._id && loggedUser && (
              <div className="row m--margin-bottom-20">
                <div className="col-lg-12" ref={stepElement}>
                  {isShowingAll ? (
                    <JourneyPath
                      journey={selectedJourney}
                      showEnrollOverlay={!isEnrolled}
                      onSelection={pickStep}
                    />
                  ) : (
                    <>
                      {checkMarkAsDone(selectedStep) && (
                        <JourneyStepDone
                          onDone={handleJourneyChange}
                          step={selectedStep}
                        />
                      )}
                      <JourneyContent
                        step={selectedStep}
                        journey={journeyOpened}
                        cards={cards}
                        countries={countries}
                        loggedUser={loggedUser}
                        languages={languages}
                        experienceDomainSkills={experienceDomainSkills}
                        hardSkills={hardSkills}
                        customTrophies={customTrophies}
                        stats={stats}
                        certificate={certificate}
                        appProducts={appProducts}
                        onChange={handleJourneyChange}
                        pspdfkitLicenseKey={pspdfkitLicenseKey}
                        vatTypes={vatTypes}
                        allLanguages={allLanguages}
                        currencies={currencies}
                        userLanguage={userLanguage}
                        directSellingActive={directSellingActive}
                        sessionContext={sessionContext}
                        journeyContext={journeyOpened._id}
                        organization={organization}
                        sessionVerifiedParticipation={
                          sessionVerifiedParticipation
                        }
                        onSkipStep={handleSkipStep}
                      />
                      {shouldShowNextButton && (
                        <div className="journey-next-button">
                          <button
                            onClick={nextStep}
                            type="button"
                            className="btn m-btn--pill btn-dynamic"
                          >
                            {t('journey_continueNextStep')}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="row back-btn-container">
            <button
              type="button"
              className="btn default button-previous"
              onClick={() => {
                window.location.href =
                  urlBack || '/academy/journeys?applyFilters=true';
              }}
            >
              <i className="fa fa-angle-left" />
              {` ${t('back')}`}
            </button>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

JourneyOverall.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  journey: PropTypes.shape(JourneyProp.propType),
  member: PropTypes.shape(UserProp.propType).isRequired,
  stepId: PropTypes.string,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  experienceDomainSkills: PropTypes.arrayOf(PropTypes.string).isRequired,
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  customTrophies: PropTypes.arrayOf(PropTypes.shape(TrophyProp.propType))
    .isRequired,
  stats: PropTypes.shape({
    performance: PropTypes.shape(PerformanceProp.propType),
    charts: PropTypes.shape(ChartsProp.propType),
  }).isRequired,
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  userCertificate: PropTypes.shape(CertificateProp.propType),
  pspdfkitLicenseKey: PropTypes.string,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  allLanguages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  userLanguage: PropTypes.string.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  orderId: PropTypes.string,
  redirect_status: PropTypes.string,
  payment_intent: PropTypes.string,
  open: PropTypes.bool,
  sessionContext: PropTypes.string,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  organization: PropTypes.shape(OrganizationProp.propType).isRequired,
  sessionVerifiedParticipation: PropTypes.bool,
  urlBack: PropTypes.string,
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  availableCurrencies: PropTypes.arrayOf(
    PropTypes.shape(CurrencyProp.propType),
  ),
};

JourneyOverall.defaultProps = {
  journey: null,
  stepId: null,
  userCertificate: null,
  pspdfkitLicenseKey: null,
  orderId: null,
  redirect_status: null,
  payment_intent: null,
  open: null,
  sessionContext: null,
  sessionVerifiedParticipation: false,
  urlBack: null,
  availableCountries: [],
  availableCurrencies: [],
};

export default compose(translate, withCSRF)(JourneyOverall);
